import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  pairwise,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlertPanelViewModel } from './components/alert-panel/alert-panel.component';
import {
  NotificationGroup,
  NotificationPreferences,
  NotificationPreferencesStateService,
  NotificationType,
} from '@usf/ngrx-alerts';
import { Router } from '@angular/router';
import { PATHS } from '@shared/constants/paths';
import { MenuController, ViewDidLeave } from '@ionic/angular';
import { ViewWillEnter } from '@ionic/angular';
import { UserDataService } from '@app/user/services/user-data.service';
import { UserKinds } from '@usf/user-types/user';
import { UpdateContactInfoDto } from '@usf/user-types/user';
import { PanAppState, UserState } from '@panamax/app-state';
import { UserService } from '@app/user/services/user.service';
import { NgForm } from '@angular/forms';

interface PreferencesPageViewModel {
  emailAddress: any;
  phoneNumber: number;
  hasActiveEmailNotifications: boolean;
  hasActivePushNotifications: boolean;
  alertPanelViewModels: AlertPanelViewModel[];
}

@Component({
  selector: 'app-notification-preferences-dashboard',
  templateUrl: './notification-preferences-dashboard.page.html',
  styleUrls: ['./notification-preferences-dashboard.page.scss'],
  standalone: false,
})
export class NotificationPreferencesDashboardPage
  implements OnInit, ViewWillEnter, ViewDidLeave
{
  private static readonly DESIRED_ORDER: readonly string[] = [
    'PAYMENT',
    'ORDER',
    'DELIVERY',
    'DIRECT',
    'TM_NOTE',
    'SERVICE_REQUEST',
  ] as const;

  public preferencesPageViewModel$: Observable<PreferencesPageViewModel> =
    EMPTY;
  private readonly pageInViewSubject: BehaviorSubject<boolean> =
    new BehaviorSubject(false);

  private readonly saveEmailAddressSubject: Subject<string> = new Subject();
  private updateContactInfoDto!: UpdateContactInfoDto;

  private userState$: Observable<UserState> = EMPTY;

  private filterTypes$: Observable<string[]> = EMPTY;

  private hideNotesSubject: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );
  private hideNotes$!: Observable<boolean>;

  private pendingNoteSubject: BehaviorSubject<{
    message: string;
    dismissable: boolean;
  } | null> = new BehaviorSubject(null);

  private userNoteSubject: BehaviorSubject<{
    message: string;
    dismissable: boolean;
  } | null> = new BehaviorSubject(null);
  public userNote$!: Observable<{ message: string; dismissable: boolean }>;

  public notificationEmail = '';
  public confirmNotificationEmail = '';

  @ViewChild('changeEmailForm') changeEmailForm!: NgForm;

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly router: Router,
    private readonly menu: MenuController,
    private readonly panAppState: PanAppState,
    private readonly notificationPreferencesStateService: NotificationPreferencesStateService,
    private readonly userDataService: UserDataService,
    private readonly userService: UserService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    this.destroyRef = destroyRef;
    this.notificationPreferencesStateService.loadNotificationPreferences();
    this.userNote$ = this.userNoteSubject
      .asObservable()
      .pipe(startWith({ message: null, dismissable: true }));
    this.hideNotes$ = this.hideNotesSubject.asObservable();
  }

  ngOnInit(): void {
    // Set the page in view to true
    this.pageInViewSubject.next(true);

    this.userState$ = this.panAppState.user$.pipe(
      filter(
        (user: UserState) =>
          !!user &&
          !!user.userKind &&
          ((user.userKind === UserKinds.External && !!user.profiles) ||
            user.userKind !== UserKinds.External),
      ),
    );

    // Get the filter types, only payment at the moment
    this.filterTypes$ = this.userService.hideInvoiceAndPayments$().pipe(
      distinctUntilChanged(),
      map(hidePayment => (hidePayment ? ['PAYMENT'] : [])),
    );

    // Save the email address when the user changes it
    this.pageInViewSubject
      .pipe(
        switchMap(inView => {
          if (!inView) {
            return EMPTY;
          } else {
            return this.saveEmailAddressSubject.asObservable();
          }
        }),
        filter(emailAddress => !!emailAddress),
        switchMap(emailAddress =>
          forkJoin([
            this.userDataService.updateContactInformation({
              ...this.updateContactInfoDto,
              email: emailAddress,
            }),
            of(emailAddress),
          ]),
        ),
        tap(([_, emailAddress]) => {
          this.updateContactInfoDto.email = emailAddress;
          this.resetFormToPristine();
        }),
        takeUntilDestroyed(this.destroyRef),
        catchError(error => {
          console.error(error);
          return EMPTY;
        }),
      )
      .subscribe();

    // Capture subjects in closure to avoid 'this' in operators
    const pendingNoteSubject = this.pendingNoteSubject;
    const userNoteSubject = this.userNoteSubject;

    // Load the preferences page view model
    this.preferencesPageViewModel$ = this.pageInViewSubject.pipe(
      switchMap(inView => {
        if (!inView) {
          return EMPTY;
        } else {
          return combineLatest<[NotificationPreferences, UserState, string[]]>([
            this.notificationPreferencesStateService
              .selectNotificationPreferences$()
              .pipe(
                startWith(null),
                tap(notificationPreferences => {
                  console.log(
                    'notificationPreferences',
                    notificationPreferences,
                  );
                }),
                pairwise(),
                tap(([prev, curr]) => {
                  if (
                    prev?.loading === true &&
                    curr?.loading === false &&
                    pendingNoteSubject.value !== null
                  ) {
                    userNoteSubject.next(this.pendingNoteSubject.value);
                    pendingNoteSubject.next(null);
                  }
                }),
                map(([_prev, curr]) => curr),
                filter(
                  notificationPreferences => !notificationPreferences.loading,
                ),
              ),
            this.userState$,
            this.filterTypes$,
          ]).pipe(
            filter(
              ([notificationPreferences, userState, _hidePayment]) =>
                notificationPreferences.ecUserId !== '' &&
                userState.userId !== '',
            ),
            // store the user state
            tap(([_notificationPreferences, userState, _filterTypes]) => {
              // Set the email address from the user state
              this.notificationEmail = userState.email ?? '';
              this.updateContactInfoDto = {
                ecUserId: userState.ecomUserId,
                email: userState.email ?? '',
                firstName: userState.firstName ?? '',
                lastName: userState.lastName ?? '',
                orgName: userState.organizationName ?? '',
              };
            }),
            // map the notification preferences to the view model
            map(([notificationPreferences, userState, filterTypes]) => ({
              userState,
              notificationPreferences,
              filterTypes,
            })),
            map(data => this.mapPreferencesToViewModel(data)),
          );
        }
      }),
    );
  }

  validateSubmit(): void {
    // Mark all fields as touched to trigger validation
    if (this.changeEmailForm) {
      Object.keys(this.changeEmailForm.controls).forEach(key => {
        this.changeEmailForm.controls[key].markAsTouched();
      });
    }

    // Check for form validity before submitting
    if (this.changeEmailForm && !this.changeEmailForm.valid) {
      return;
    }

    // Double-check that emails match
    if (this.notificationEmail !== this.confirmNotificationEmail) {
      return;
    }

    this.saveEmailAddressSubject.next(this.notificationEmail);
  }

  cancelEmailChange(): void {
    this.resetFormToPristine();
  }

  onAlertPanelClick(alertType: string) {
    this.router.navigate([
      PATHS.NOTIFICATIONS,
      PATHS.NOTIFICATION_PREFERENCES,
      'type-overview',
      alertType,
    ]);
  }

  onSettingsButtonClick() {
    this.menu.open('flyout');
  }

  onCloseMenuClick() {
    this.menu.close('flyout').then(() => {
      this.resetFormToPristine();
    });
  }

  onDismissNoteChange() {
    this.userNoteSubject.next({
      message:
        'All alerts have been turned off for all customers. Opt in to alerts by selecting an alert category.',
      dismissable: true,
    });
  }

  onDismissNoteClose() {
    this.userNoteSubject.next({
      message: null,
      dismissable: true,
    });
  }

  ionViewWillEnter() {
    this.pageInViewSubject.next(true);
    //Set the page title
    this.panAppState.setPageTitle(
      'i18n.notifications.preferences.alertsDashboard',
    );
  }

  ionViewDidLeave() {
    this.pageInViewSubject.next(false);
  }

  trackByAlertPanel(
    _index: number,
    panelViewModel: AlertPanelViewModel,
  ): string {
    return panelViewModel.alertType;
  }

  private mapPreferencesToViewModel({
    userState,
    notificationPreferences,
    filterTypes,
  }: {
    userState: UserState;
    notificationPreferences: NotificationPreferences;
    filterTypes: string[];
  }): PreferencesPageViewModel {
    return {
      emailAddress: userState.email,
      phoneNumber: 212,
      hasActiveEmailNotifications: notificationPreferences.emailActive,
      hasActivePushNotifications: notificationPreferences.pushActive,
      alertPanelViewModels: notificationPreferences.types
        .filter(
          (notificationType: NotificationType) =>
            !filterTypes.includes(notificationType.type),
        )
        .map<AlertPanelViewModel>((notificaionType: NotificationType) => {
          return {
            alertType: notificaionType.type,
            alertEnabled: notificaionType.groups.some(
              (group: NotificationGroup) => group.isEnabled,
            ),
            subscribedCount: notificaionType.groups.filter(
              (group: NotificationGroup) => group.isEnabled,
            ).length,
            totalCount: notificaionType.groups.length,
            disabled: false,
          };
        })
        .sort((a, b) => {
          const aIndex =
            NotificationPreferencesDashboardPage.DESIRED_ORDER.indexOf(
              a.alertType,
            );
          const bIndex =
            NotificationPreferencesDashboardPage.DESIRED_ORDER.indexOf(
              b.alertType,
            );

          // Handle items not in the predefined order (put them at the end)
          if (aIndex === -1 && bIndex === -1) return 0;
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;

          return aIndex - bIndex;
        }),
    };
  }

  onOptOutAllChange() {
    this.notificationPreferencesStateService.setBulkNotificationPreferences(
      true,
      true,
    );
    this.pendingNoteSubject.next({
      message:
        'All alerts have been turned off for all customers. Opt in to alerts by selecting an alert category.',
      dismissable: true,
    });
  }

  onOptOutEmailChange(): void {
    this.notificationPreferencesStateService.setBulkNotificationPreferences(
      true,
      false,
    );
    this.pendingNoteSubject.next({
      message:
        'All alerts have been turned off for all customers. Opt in to alerts by selecting an alert category.',
      dismissable: true,
    });
  }

  onOptOutPushChange(): void {
    this.notificationPreferencesStateService.setBulkNotificationPreferences(
      false,
      true,
    );
    this.pendingNoteSubject.next({
      message:
        'All alerts have been turned off for all customers. Opt in to alerts by selecting an alert category.',
      dismissable: true,
    });
  }

  private resetFormToPristine(): void {
    if (this.changeEmailForm) {
      // Reset validation states while keeping current values
      this.changeEmailForm.control.markAsPristine();
      this.changeEmailForm.control.markAsUntouched();

      this.changeEmailForm.controls['notificationEmail'].markAsPristine();
      this.changeEmailForm.controls['notificationEmail'].markAsUntouched();
      this.changeEmailForm.controls['notificationEmail'].reset(
        this.updateContactInfoDto?.email ?? '',
      );

      this.changeEmailForm.controls[
        'confirmNotificationEmail'
      ].markAsPristine();
      this.changeEmailForm.controls[
        'confirmNotificationEmail'
      ].markAsUntouched();
      this.changeEmailForm.controls['confirmNotificationEmail'].reset('');

      this.changeDetectorRef.detectChanges();
    }
  }
}
